/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The reports list.
 *
 * Replaces layouts/crm/Reports.js, which was six hardcoded cards linking to
 * five hardcoded pages — nothing on it came from the database and nothing on
 * it could be changed.
 *
 * Folders down the left, reports in the middle. The left rail is a fixed set
 * of scopes (all, mine, shared, templates) plus the tenant's own folders,
 * because "which folder" and "whose report" are different questions and
 * collapsing them into one tree makes both harder to answer.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, InputAdornment, Menu, MenuItem, Snackbar,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AddRounded, AssessmentOutlined, AutoAwesome, BarChartRounded, ClearRounded,
  ContentCopyRounded, CreateNewFolderRounded, DeleteOutlineRounded,
  DriveFileMoveOutlined,
  EditOutlined, FolderOutlined, InboxRounded, MoreVertRounded,
  PeopleOutlineRounded, PersonOutlineRounded, PlayArrowRounded,
  SearchRounded, ShareOutlined, StarBorderRounded,
} from '@mui/icons-material';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import colors from 'styles/tokens/colors';
import CreateWithAiDialog from 'components/AiAssistant/CreateWithAiDialog';
import { useAiAssistant } from 'context/AiAssistantContext';
import ShareDialog from './ShareDialog';

/** Fixed scopes, always present, above whatever folders the tenant made. */
const SCOPES = [
  { id: 'all', label: 'All reports', icon: AssessmentOutlined },
  { id: 'mine', label: 'Created by me', icon: PersonOutlineRounded },
  { id: 'shared', label: 'Shared with me', icon: PeopleOutlineRounded },
  { id: 'templates', label: 'Templates', icon: StarBorderRounded },
];

export default function ReportsHome() {
  const { axiosInstance, user } = useAuth();
  const navigate = useNavigate();
  const { enabled: aiEnabled } = useAiAssistant();
  const [aiOpen, setAiOpen] = useState(false);

  const [reports, setReports] = useState([]);
  const [folders, setFolders] = useState([]);
  const [scope, setScope] = useState('all');
  const [folderId, setFolderId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [menu, setMenu] = useState({ anchor: null, report: null });
  const [sharing, setSharing] = useState(null);
  const [newFolder, setNewFolder] = useState(null);
  const [moving, setMoving] = useState(null);
  const [seedInfo, setSeedInfo] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reportsRes, foldersRes] = await Promise.all([
        axiosInstance.get('/analytics/reports', { params: { limit: 500 } }),
        axiosInstance.get('/analytics/folders'),
      ]);
      setReports(Array.isArray(reportsRes.data) ? reportsRes.data : []);
      setFolders(Array.isArray(foldersRes.data) ? foldersRes.data : []);
      setError(null);
    } catch (err) {
      // "Not entitled" and "backend down" need different responses from
      // whoever is reading this, so they must not both render as an empty list.
      setError(
        err?.response?.status === 403
          ? 'You do not have permission to view reports.'
          : 'Reports could not be loaded. Check that the backend is running.',
      );
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => { load(); }, [load]);

  // Offer to install the library when the tenant has no templates yet, rather
  // than showing an empty Templates folder and leaving them to guess.
  useEffect(() => {
    if (loading || error) return;
    if (reports.some((r) => r.is_template)) { setSeedInfo(null); return; }
    axiosInstance.get('/analytics/templates/available')
      .then(({ data }) => setSeedInfo(Array.isArray(data) ? data : []))
      .catch(() => setSeedInfo(null));
  }, [loading, error, reports, axiosInstance]);

  const installTemplates = async () => {
    setSeeding(true);
    try {
      const { data } = await axiosInstance.post('/analytics/templates/seed');
      setToast({ severity: 'success',
                 message: `${data.created} report templates installed.` });
      setSeedInfo(null);
      await load();
    } catch (err) {
      setToast({ severity: 'error', message: readError(err,
        'The templates could not be installed. You may need administrator access.') });
    } finally {
      setSeeding(false);
    }
  };

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return reports.filter((report) => {
      if (folderId != null) {
        if (report.folder_id !== folderId) return false;
      } else if (scope === 'templates') {
        if (!report.is_template) return false;
      } else if (scope === 'mine') {
        if (report.created_by !== user?.id || report.is_template) return false;
      } else if (scope === 'shared') {
        // Shared with me: reachable, but neither mine nor a built-in.
        if (report.created_by === user?.id || report.is_template) return false;
      } else if (report.is_template) {
        // "All reports" means the tenant's own work. 48 built-ins would drown it.
        return false;
      }
      if (!term) return true;
      return `${report.name} ${report.description ?? ''} ${report.primary_object}`
        .toLowerCase().includes(term);
    });
  }, [reports, scope, folderId, search, user]);

  const counts = useMemo(() => ({
    all: reports.filter((r) => !r.is_template).length,
    mine: reports.filter((r) => r.created_by === user?.id && !r.is_template).length,
    shared: reports.filter((r) => r.created_by !== user?.id && !r.is_template).length,
    templates: reports.filter((r) => r.is_template).length,
  }), [reports, user]);

  const cloneReport = async (report) => {
    try {
      const { data } = await axiosInstance.post(`/analytics/reports/${report.id}/clone`, {
        name: `${report.name} (copy)`,
      });
      setToast({ severity: 'success', message: 'Copied. Opening your version.' });
      navigate(`/reports/${data.id}/edit`);
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That report could not be copied.') });
    }
  };

  const deleteReport = async (report) => {
    try {
      await axiosInstance.delete(`/analytics/reports/${report.id}`);
      setToast({ severity: 'success', message: 'Report deleted.' });
      await load();
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That report could not be deleted.') });
    }
  };

  const moveReport = async (report, folderId) => {
    try {
      await axiosInstance.patch(`/analytics/reports/${report.id}`, {
        folder_id: folderId,
      });
      setMoving(null);
      setToast({ severity: 'success',
                 message: folderId
                   ? 'Moved.'
                   : 'Taken out of its folder.' });
      await load();
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That report could not be moved.') });
    }
  };

  const createFolder = async () => {
    const name = (newFolder ?? '').trim();
    if (!name) return;
    try {
      await axiosInstance.post('/analytics/folders', { name, folder_kind: 'private' });
      setNewFolder(null);
      await load();
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That folder could not be created.') });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between"
             gap={2} mb={3} flexWrap="wrap">
          <Box>
            <Typography variant="h4" fontWeight={700} lineHeight={1.2}>Reports</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Build a report on any object, chart it, and put it on a dashboard.
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            {aiEnabled && (
              <MDButton variant="outlined" color="info" onClick={() => setAiOpen(true)}>
                <AutoAwesome sx={{ mr: 0.75 }} fontSize="small" /> Create with AI
              </MDButton>
            )}
            <MDButton variant="gradient" color="info" onClick={() => navigate('/reports/new')}>
              <AddRounded sx={{ mr: 0.75 }} /> New report
            </MDButton>
          </Box>
        </Box>
        <CreateWithAiDialog open={aiOpen} kind="report" onClose={() => setAiOpen(false)} />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {seedInfo?.length > 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <MDButton size="small" variant="gradient" color="info"
                        disabled={seeding} onClick={installTemplates}>
                {seeding ? 'Installing…' : 'Install them'}
              </MDButton>
            }
          >
            {seedInfo.length} ready-made reports suit this organisation — pipeline,
            lead sources, case aging and more. Install them and edit or copy any
            of them.
          </Alert>
        )}

        <Box display="flex" gap={3} alignItems="flex-start" flexWrap={{ xs: 'wrap', md: 'nowrap' }}>
          {/* ------------------------------------------------- left rail */}
          <Box
            sx={{
              width: { xs: '100%', md: 240 }, flexShrink: 0,
              border: '1px solid', borderColor: 'divider', borderRadius: '14px',
              p: 1, backgroundColor: colors.background.white,
            }}
          >
            {SCOPES.map(({ id, label, icon: Icon }) => (
              <RailItem
                key={id} icon={Icon} label={label} count={counts[id]}
                active={folderId == null && scope === id}
                onClick={() => { setScope(id); setFolderId(null); }}
              />
            ))}

            <Divider sx={{ my: 1 }} />
            <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={0.5}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                FOLDERS
              </Typography>
              <Tooltip title="New folder">
                <IconButton size="small" onClick={() => setNewFolder('')}>
                  <CreateNewFolderRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {!folders.length ? (
              <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 1, display: 'block' }}>
                No folders yet.
              </Typography>
            ) : (
              <FolderTree
                folders={folders} activeId={folderId}
                onSelect={(id) => { setFolderId(id); setScope('all'); }}
                reports={reports}
              />
            )}
          </Box>

          {/* ---------------------------------------------------- list */}
          <Box flex={1} minWidth={0}>
            <TextField
              size="small" fullWidth placeholder="Search reports…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearRounded fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {loading ? (
              <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
            ) : !visible.length ? (
              <EmptyState scope={scope} search={search} onCreate={() => navigate('/reports/new')} />
            ) : (
              <Box display="flex" flexDirection="column" gap={1}>
                {visible.map((report) => (
                  <ReportRow
                    key={report.id} report={report}
                    onOpen={() => navigate(`/reports/${report.id}`)}
                    onMenu={(anchor) => setMenu({ anchor, report })}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </MDBox>

      {/* ------------------------------------------------------ row menu */}
      <Menu
        anchorEl={menu.anchor} open={Boolean(menu.anchor)}
        onClose={() => setMenu({ anchor: null, report: null })}
      >
        <MenuItem onClick={() => { navigate(`/reports/${menu.report.id}`); setMenu({ anchor: null }); }}>
          <PlayArrowRounded fontSize="small" sx={{ mr: 1 }} /> Run
        </MenuItem>
        {/* A template is the shared floor everyone clones from; editing one in
            place would change what every other user sees. */}
        {menu.report?.access !== 'view' && !menu.report?.is_template && (
          <MenuItem onClick={() => { navigate(`/reports/${menu.report.id}/edit`); setMenu({ anchor: null }); }}>
            <EditOutlined fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
        )}
        <MenuItem onClick={() => { cloneReport(menu.report); setMenu({ anchor: null }); }}>
          <ContentCopyRounded fontSize="small" sx={{ mr: 1 }} /> Make a copy
        </MenuItem>
        {menu.report?.access !== 'view' && !menu.report?.is_template && (
          <MenuItem onClick={() => { setMoving(menu.report); setMenu({ anchor: null }); }}>
            <DriveFileMoveOutlined fontSize="small" sx={{ mr: 1 }} /> Move to folder
          </MenuItem>
        )}
        {menu.report?.access === 'manage' && (
          <MenuItem onClick={() => { setSharing(menu.report); setMenu({ anchor: null }); }}>
            <ShareOutlined fontSize="small" sx={{ mr: 1 }} /> Share
          </MenuItem>
        )}
        {menu.report?.access === 'manage' && !menu.report?.is_template && (
          <MenuItem
            onClick={() => { deleteReport(menu.report); setMenu({ anchor: null }); }}
            sx={{ color: colors.error.text }}
          >
            <DeleteOutlineRounded fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      <ShareDialog
        open={Boolean(sharing)} onClose={() => setSharing(null)}
        resourceType="report" resource={sharing}
      />

      <Dialog open={newFolder !== null} onClose={() => setNewFolder(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>New folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="Folder name" sx={{ mt: 1 }}
            value={newFolder ?? ''} onChange={(e) => setNewFolder(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && createFolder()}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="text" color="dark" onClick={() => setNewFolder(null)}>Cancel</MDButton>
          <MDButton variant="gradient" color="info" onClick={createFolder}
                    disabled={!(newFolder ?? '').trim()}>
            Create
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(moving)} onClose={() => setMoving(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>
          Move “{moving?.name}”
        </DialogTitle>
        <DialogContent>
          {!folders.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              You have no folders yet. Create one first.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
              {/* Explicitly offered, because a report with no folder is the
                  normal state and there has to be a way back to it. */}
              <FolderChoice
                label="No folder" icon={InboxRounded}
                selected={moving?.folder_id == null}
                onClick={() => moveReport(moving, null)}
              />
              {flattenFolders(folders).map(({ folder, depth }) => (
                <FolderChoice
                  key={folder.id}
                  label={folder.name}
                  icon={FolderOutlined}
                  depth={depth}
                  selected={moving?.folder_id === folder.id}
                  disabled={folder.is_system}
                  hint={folder.is_system ? 'Built-in' : undefined}
                  onClick={() => moveReport(moving, folder.id)}
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="text" color="dark" onClick={() => setMoving(null)}>Cancel</MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

/* --------------------------------------------------------------- pieces */

function RailItem({ icon: Icon, label, count, active, onClick, depth = 0 }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer',
        px: 1.5, py: 1, pl: 1.5 + depth * 1.5, borderRadius: '10px',
        backgroundColor: active ? colors.chip.selectedBackground : 'transparent',
        color: active ? colors.chip.selectedText : colors.text.dark,
        fontWeight: active ? 700 : 500,
        '&:hover': { backgroundColor: active ? colors.chip.selectedBackground : colors.background.offWhite },
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
      <Typography variant="body2" flex={1} noWrap sx={{ fontWeight: 'inherit', color: 'inherit' }}>
        {label}
      </Typography>
      {count > 0 && (
        <Typography variant="caption" color="text.secondary">{count}</Typography>
      )}
    </Box>
  );
}

function FolderTree({ folders, activeId, onSelect, reports, depth = 0 }) {
  return folders.map((folder) => (
    <Box key={folder.id}>
      <RailItem
        icon={FolderOutlined} label={folder.name} depth={depth}
        count={reports.filter((r) => r.folder_id === folder.id).length}
        active={activeId === folder.id}
        onClick={() => onSelect(folder.id)}
      />
      {folder.children?.length > 0 && (
        <FolderTree
          folders={folder.children} activeId={activeId} onSelect={onSelect}
          reports={reports} depth={depth + 1}
        />
      )}
    </Box>
  ));
}

function ReportRow({ report, onOpen, onMenu }) {
  return (
    <Box
      onClick={onOpen}
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer',
        px: 2, py: 1.5, border: '1px solid', borderColor: 'divider',
        borderRadius: '12px', backgroundColor: colors.background.white,
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': { borderColor: colors.brand.primary,
                     boxShadow: '0 2px 10px rgba(21,101,200,.08)' },
      }}
    >
      <Box
        sx={{
          width: 36, height: 36, flexShrink: 0, borderRadius: '10px',
          display: 'grid', placeItems: 'center',
          backgroundColor: colors.brand.primaryContainer,
          color: colors.brand.primary,
        }}
      >
        {report.report_kind === 'tabular'
          ? <AssessmentOutlined sx={{ fontSize: 19 }} />
          : <BarChartRounded sx={{ fontSize: 19 }} />}
      </Box>

      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" fontWeight={600} noWrap>{report.name}</Typography>
          {report.is_template && (
            <Chip size="small" label="Built-in" sx={{
              height: 18, fontSize: 10, fontWeight: 700,
              backgroundColor: colors.status.info.background,
              color: colors.status.info.text,
            }} />
          )}
          {report.charts?.length > 0 && (
            <Tooltip title={`${report.charts.length} chart(s)`}>
              <BarChartRounded sx={{ fontSize: 14, color: colors.text.light }} />
            </Tooltip>
          )}
        </Box>
        {report.description && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {report.description}
          </Typography>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {report.last_run_at
          ? `Run ${new Date(report.last_run_at).toLocaleDateString()}`
          : 'Never run'}
      </Typography>

      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); onMenu(e.currentTarget); }}
      >
        <MoreVertRounded fontSize="small" />
      </IconButton>
    </Box>
  );
}

function EmptyState({ scope, search, onCreate }) {
  const message = search
    ? 'No reports match that search.'
    : {
        templates: 'No built-in reports are installed yet.',
        mine: 'You have not built a report yet.',
        shared: 'Nobody has shared a report with you.',
      }[scope] ?? 'No reports here yet.';

  return (
    <Box sx={{ p: 6, textAlign: 'center', border: '1px dashed',
               borderColor: 'divider', borderRadius: '14px' }}>
      <InboxRounded sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
      <Typography variant="h6" gutterBottom>{message}</Typography>
      {!search && (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Start from any object — leads, deals, cases, orders — pick the
            columns you want and filter it down.
          </Typography>
          <MDButton variant="gradient" color="info" onClick={onCreate}>
            <AddRounded sx={{ mr: 0.75 }} /> New report
          </MDButton>
        </>
      )}
    </Box>
  );
}

/** One row in the move dialog. */
function FolderChoice({ label, icon: Icon, depth = 0, selected, disabled, hint,
                        onClick }) {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 1.5, py: 1, pl: 1.5 + depth * 2, borderRadius: '10px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        backgroundColor: selected ? colors.chip.selectedBackground : 'transparent',
        color: selected ? colors.chip.selectedText : colors.text.dark,
        fontWeight: selected ? 700 : 500,
        '&:hover': { backgroundColor: disabled
          ? 'transparent'
          : (selected ? colors.chip.selectedBackground : colors.background.offWhite) },
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
      <Typography variant="body2" flex={1} noWrap
                  sx={{ fontWeight: 'inherit', color: 'inherit' }}>
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
      )}
      {selected && (
        <Typography variant="caption" color="text.secondary">current</Typography>
      )}
    </Box>
  );
}

/** The folder tree as a flat list with indent depth, for a picker. */
function flattenFolders(folders, depth = 0) {
  return folders.flatMap((folder) => [
    { folder, depth },
    ...flattenFolders(folder.children ?? [], depth + 1),
  ]);
}

function readError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}
